import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    FormField,
    FormHelp,
    Input,
    Label,
} from '@zillow/constellation';

import { AddressSelectors } from 'app/shared/selectors';
import AddressActions from 'app/shared/actions/AddressActions';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import RoomForRent from 'app/shared/components/Modal/AddPropertyModal/components/RoomForRent';
import StringConstants from 'app/shared/constants/StringConstants';
import CustomerSupportErrorMessage
    from 'app/shared/components/Modal/AddPropertyModal/components/CustomerSupportErrorMessage';

import { StyledAutoComplete, StyledSelect } from './StyledComponents';

const {
    PROPERTY_TYPES: {
        COMMUNITY,
        CONDO,
        HOUSE,
        TOWNHOUSE,
    },
} = PropertyConstants;
const MIN_LENGTH_REQUIRED_BY_API = 7;

export const defaultErrorObj = {
    address: '',
    unitNumber: '',
    numberOfUnits: '',
    customerId: '',
};

const propTypes = {
    behavior: PropTypes.oneOf(['Add', 'Edit']).isRequired,
    listing: PropTypes.shape({
        address: PropTypes.string.isRequired,
        isAddressEditable: PropTypes.bool.isRequired,
        isEditable: PropTypes.bool.isRequired,
        isRoomForRent: PropTypes.bool.isRequired,
        numUnits: PropTypes.string.isRequired,
        propertyType: PropTypes.string.isRequired,
        customerId: PropTypes.string,
        duplicateAccountListingId: PropTypes.string,
        unit: PropTypes.string,
        // For edit
        listingAlias: PropTypes.string,
    }).isRequired,
    contracts: PropTypes.arrayOf(PropTypes.shape({
        customerId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
    })),
    errorObj: PropTypes.object.isRequired,
    mktPath: PropTypes.string,
    verifyIsValidAddress: PropTypes.func.isRequired,
    onListingChange: PropTypes.func.isRequired,
    onCheckLocationRestriction: PropTypes.func.isRequired,
    onCustomerSupportClick: PropTypes.func.isRequired,
};

const defaultProps = {
    mktPath: null,
    contracts: [],
};

const AddPropertyForm = ({
    behavior,
    listing,
    contracts,
    errorObj,
    mktPath,
    verifyIsValidAddress,
    onListingChange,
    onCheckLocationRestriction,
    onCustomerSupportClick,
}) => {
    const dispatch = useDispatch();
    const addressList = useSelector(state => AddressSelectors.makeAddressList(state));
    const {
        address,
        isAddressEditable,
        isEditable,
        isRoomForRent,
        numUnits,
        propertyType,
        unit,
        customerId,
    } = listing;
    const isFormDisabled = !isEditable || !isAddressEditable;


    const handleChangeListing = (keyName, newValue, verifyLocationRestriction = false) => {
        onListingChange(keyName, newValue);
        if (verifyLocationRestriction) {
            onCheckLocationRestriction(keyName, newValue);
        }
    };

    const handleSelectAddress = (newAddress) => {
        handleChangeListing('address', newAddress.name, true);
    };

    const handleAddressChange = (value) => {
        handleChangeListing('address', value, false);

        if (value.length < MIN_LENGTH_REQUIRED_BY_API) {
            return;
        }

        dispatch(AddressActions.autocomplete(value));
    };

    const handleUpdateMFNumUnits = (e) => {
        handleChangeListing('numUnits', e.target.value, false);
    };

    const handleUpdateUnit = (e) => {
        const newUnit = e.target.value;
        const unitLengthLimit = 20;
        if (newUnit.length <= unitLengthLimit) {
            handleChangeListing('unit', newUnit, false);
        }
    };

    const handleUpdateUnitBlur = async () => {
        await verifyIsValidAddress();
        onCheckLocationRestriction();
    };

    const handleToggleRoomForRent = () => {
        handleChangeListing('isRoomForRent', !isRoomForRent, true);
    };

    const handleUpdatePropertyType = (e) => {
        handleChangeListing('propertyType', e.target.value, true);
    };

    const handleUpdateCustomerId = (e) => {
        handleChangeListing('customerId', e.target.value, false);
    };

    const isCommunityType = propertyType === COMMUNITY;
    const disableForCommunityType = behavior === 'Edit' && isCommunityType;
    const disableForNonCommunityType = behavior === 'Edit' && !isCommunityType;
    const isRoomForRentDisabled = (behavior === 'Edit') ||
                                  isFormDisabled ||
                                  (mktPath === StringConstants.MARKETING_PATH.LEASES);

    return (
        <div>
            <FormField paddingY={1.5} error={!!errorObj.address}>
                <Label>Property address</Label>
                <StyledAutoComplete
                    items={addressList}
                    placeholder=""
                    onChange={handleAddressChange}
                    onSelect={handleSelectAddress}
                    value={address}
                    disabled={isFormDisabled}
                />
                <FormHelp error={!!errorObj.address}>{errorObj.address}</FormHelp>
            </FormField>
            {!isAddressEditable && <CustomerSupportErrorMessage modalClose={onCustomerSupportClick} />}
            {!isCommunityType && (
                <FormField paddingY={1.5} error={!!errorObj.unitNumber}>
                    <Label>Unit number</Label>
                    <Input
                        data-test="unit-number"
                        aria-label="Unit number"
                        placeholder=""
                        onBlur={handleUpdateUnitBlur}
                        onChange={handleUpdateUnit}
                        value={unit}
                        disabled={isFormDisabled}
                    />
                    <FormHelp error={!!errorObj.unitNumber}>{errorObj.unitNumber}</FormHelp>
                </FormField>
            )}
            <FormField paddingY={1.5} error={!!errorObj.propertyType}>
                <Label>Property type</Label>
                <StyledSelect
                    aria-label="Property type"
                    value={propertyType || 'Please select'}
                    onChange={handleUpdatePropertyType}
                    disabled={isFormDisabled}
                >
                    <option disabled={true}>Please select</option>
                    <option disabled={disableForCommunityType} value={HOUSE}>{HOUSE}</option>
                    <option disabled={disableForCommunityType} value={CONDO}>{CONDO}</option>
                    <option disabled={disableForCommunityType} value={TOWNHOUSE}>{TOWNHOUSE}</option>
                    <option disabled={disableForNonCommunityType} value={COMMUNITY}>{COMMUNITY}</option>
                </StyledSelect>
                <FormHelp error={!!errorObj.propertyType}>{errorObj.propertyType}</FormHelp>
            </FormField>
            {behavior === 'Add' && (contracts.length > 1 || errorObj.customerId) && (
                <FormField paddingY={1.5} error={!!errorObj.customerId} data-test="active-contracts">
                    <Label>Active Contracts</Label>
                    <StyledSelect
                        aria-label="Active contracts"
                        value={customerId || 'Please select'}
                        onChange={handleUpdateCustomerId}
                        disabled={isFormDisabled}
                    >
                        {contracts.map(contract => (
                            <option key={contract.customerId} value={contract.customerId}>{contract.name}</option>
                        ))}
                    </StyledSelect>
                    <FormHelp error={!!errorObj.customerId}>{errorObj.customerId}</FormHelp>
                </FormField>
            )}
            {isCommunityType ? (
                <FormField paddingTop={1.5} paddingBottom="xs" error={!!errorObj.numberOfUnits}>
                    <Label>Number of units</Label>
                    <Input
                        data-test="number-of-units"
                        error={errorObj.numberOfUnits}
                        onChange={handleUpdateMFNumUnits}
                        onBlur={onCheckLocationRestriction}
                        value={numUnits}
                        disabled={isFormDisabled}
                    />
                    <FormHelp error={!!errorObj.numberOfUnits}>{errorObj.numberOfUnits}</FormHelp>
                </FormField>
            ) : (
                <RoomForRent
                    isRoomForRentChecked={isRoomForRent}
                    isRoomForRentDisabled={isRoomForRentDisabled}
                    toggleRoomForRentChecked={handleToggleRoomForRent}
                />
            )}
        </div>
    );
};

AddPropertyForm.propTypes = propTypes;
AddPropertyForm.defaultProps = defaultProps;

export default AddPropertyForm;
